// src/components/dashboard/EditDossier.js

import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/config";

const INSTALLATION_TYPES = [
  "Sur toiture tuile",
  "Sur bac acier",
  "Toiture terrasse",
  "Au sol",
  "En ombrière",
  "Intégrée au bâti"
];

export default function EditDossier() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const fetchDossierAndUsers = async () => {
      const docRef = doc(db, "dossiers", id);
      const snapshot = await getDoc(docRef);
      if (snapshot.exists()) {
        setDossier({ id: snapshot.id, ...snapshot.data() });
      }
      const usersSnap = await getDocs(collection(db, "users"));
      const usersList = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(usersList);
      const clientsSnap = await getDocs(collection(db, "clients"));
      const clientsList = clientsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClients(clientsList);
    };
    fetchDossierAndUsers();
  }, [id]);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyCo-y50tiJe4eQoNqlXJlr3_hGeG2DB5sk&libraries=places`;
    script.async = true;
    script.onload = () => {
      if (!window.google) return;
      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current);
      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        if (place.geometry && place.geometry.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          setDossier(prev => ({
            ...prev,
            siteAddress: place.formatted_address,
            gps: `${lat}, ${lng}`
          }));
        }
      });
    };
    document.body.appendChild(script);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, selectedOptions } = e.target;
    if (type === "select-multiple") {
      const selected = Array.from(selectedOptions).map(opt => opt.value);
      setDossier(prev => ({ ...prev, [name]: selected }));
    } else {
      setDossier(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const docRef = doc(db, "dossiers", id);
      await updateDoc(docRef, dossier);
      alert("Dossier mis à jour avec succès.");
      navigate("/admin/dossiers");
    } catch (error) {
      console.error("Erreur lors de la mise à jour :", error);
      alert("Erreur lors de la mise à jour du dossier");
    }
  };

  if (!dossier) return <div>Chargement...</div>;

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Modifier le Dossier</h2>
      <form onSubmit={handleUpdate} className="grid grid-cols-2 gap-4">
        <label className="col-span-2">Titre du dossier</label>
        <input name="title" value={dossier.title || ""} onChange={handleChange} className="col-span-2 border p-2 rounded" required />

        <label className="col-span-2">Description</label>
        <textarea name="description" value={dossier.description || ""} onChange={handleChange} className="col-span-2 border p-2 rounded" rows={3} />

        <label className="col-span-2">Adresse du chantier</label>
        <input ref={inputRef} name="siteAddress" value={dossier.siteAddress || ""} onChange={handleChange} className="col-span-2 border p-2 rounded" />

        <label className="col-span-2">Coordonnées GPS</label>
        <input name="gps" value={dossier.gps || ""} readOnly className="col-span-2 border p-2 rounded bg-gray-100" />

        <label className="col-span-2">Type d'installation</label>
        <select name="installationType" multiple value={dossier.installationType || []} onChange={handleChange} className="col-span-2 border p-2 rounded h-40">
          {INSTALLATION_TYPES.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>

        <label className="col-span-2">Type de client</label>
        <select name="typeClient" value={dossier.typeClient || ""} onChange={handleChange} className="col-span-2 border p-2 rounded">
          <option value="">-- Choisir un type --</option>
          <option value="particulier">Particulier</option>
          <option value="professionnel">Professionnel</option>
          <option value="collectivite">Collectivité</option>
        </select>

        <label className="col-span-2">Source du contact</label>
        <input name="sourceContact" value={dossier.sourceContact || ""} onChange={handleChange} className="col-span-2 border p-2 rounded" />

        <label className="col-span-2">Montant estimé (€)</label>
        <input name="montant" value={dossier.montant || ""} onChange={handleChange} className="col-span-2 border p-2 rounded" />

        <label className="col-span-2">Puissance souhaitée (kWc)</label>
        <input name="systemCapacity" value={dossier.systemCapacity || ""} onChange={handleChange} className="col-span-2 border p-2 rounded" />

        <label className="col-span-2">Date de livraison prévue</label>
        <input name="materialDeliveryDate" value={dossier.materialDeliveryDate || ""} onChange={handleChange} type="date" className="col-span-2 border p-2 rounded" />

        <label className="col-span-2">Date de début des travaux</label>
        <input name="startDate" value={dossier.startDate || ""} onChange={handleChange} type="date" className="col-span-2 border p-2 rounded" />

        <label className="col-span-2">Date limite de clôture</label>
        <input name="deadline" value={dossier.deadline || ""} onChange={handleChange} type="date" className="col-span-2 border p-2 rounded" />

        <label className="col-span-2">Notes internes</label>
        <textarea name="notes" value={dossier.notes || ""} onChange={handleChange} className="col-span-2 border p-2 rounded" rows={3} />

        <button type="submit" className="col-span-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">Mettre à jour</button>
      </form>
    </div>
  );
}
